import React, {
  FunctionComponent,
  useState,
  useCallback,
  useEffect
} from 'react';
import {IntlProvider} from 'react-intl';
import {HashRouter as Router, Switch, Route, Redirect} from 'react-router-dom';
import {SWRConfig} from 'swr';
import {GoogleMapProvider} from '@ubilabs/google-maps-react-hooks';

import {Locale} from '../../../shared/types/locales';

import Header from './components/header/header';
import MapMarkers from './components/map-markers/map-markers';
import CookieBanner from './components/cookie-banner/cookie-banner';
import MapCanvas from './components/map-canvas/map-canvas';
import MapControls from './components/map-controls/map-controls';
import InfoScreen from './components/info-screen/info-screen';
import ImprintScreen from './components/imprint-screen/imprint-screen';
import PrivacyScreen from './components/privacy-screen/privacy-screen';
import TermsScreen from './components/terms-screen/terms-screen';
import WorkOfArtDetails from './components/work-of-art-details/work-of-art-details';
import CitizenScienceScreen from './components/citizen-science/citizen-science-screen';

import FiltersProvider from '../hooks/useFilters';

import {GOOGLE_MAPS_API_KEY, mapOptions} from '../constants/map';

import {fetcher} from './libs/api-utilities';

import translations from '../../../shared/i18n/translations';

import '../styles/variables.module.css';
import '../styles/main.module.css';
import '../styles/map-controls.module.css';
import '../styles/map-markers.module.css';

const DEFAULT_LOCALE = Locale.de;

const App: FunctionComponent<Record<string, unknown>> = () => {
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);
  const mapRef = useCallback(node => {
    node && setMapContainer(node);
  }, []);

  const [locale, setLocale] = useState<Locale>(DEFAULT_LOCALE);
  const [darkMode, setDarkMode] = useState<boolean>(
    window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    document.body.dataset.theme = darkMode ? 'dark' : 'light';
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prevState => !prevState);
  };

  return (
    <React.StrictMode>
      <SWRConfig value={{fetcher}}>
        <IntlProvider
          locale={locale}
          messages={translations[locale]}
          defaultLocale={DEFAULT_LOCALE}>
          <Router>
            <FiltersProvider>
              <GoogleMapProvider
                googleMapsAPIKey={GOOGLE_MAPS_API_KEY}
                mapContainer={mapContainer}
                options={mapOptions}
                libraries={['places']}>
                <Header
                  toggleDarkMode={toggleDarkMode}
                  onChangeLocale={setLocale}
                />
                <main>
                  <div>
                    <MapCanvas ref={mapRef} />
                    <MapControls />
                    <MapMarkers />
                  </div>
                  <Switch>
                    <Route path={['/', '/info']} exact>
                      <InfoScreen />
                    </Route>

                    <Route path="/citizen-science">
                      <CitizenScienceScreen />
                    </Route>

                    <Route path="/imprint">
                      <ImprintScreen />
                    </Route>

                    <Route path="/privacy">
                      <PrivacyScreen />
                    </Route>

                    <Route path="/terms-of-use">
                      <TermsScreen />
                    </Route>

                    <Route path="/works/:workId">
                      <WorkOfArtDetails />
                    </Route>

                    <Redirect to="/" />
                  </Switch>
                </main>
              </GoogleMapProvider>
            </FiltersProvider>
            <CookieBanner />
          </Router>
        </IntlProvider>
      </SWRConfig>
    </React.StrictMode>
  );
};

export default App;
